import { css } from 'aphrodite';
import moment from 'moment';
import { DataDisplay } from '../../components/DataDisplay';
import { formatCurrency } from '../../helpers/formatCurrency';
import { styleSheet as advancedReportingStyleSheet } from '../../styles';
import { IInForcePoint, IRetention } from './historyData';
import { styleSheet } from './styles';

interface IInForce {
	start: IInForcePoint | null;
	end: IInForcePoint | null;
}

const signed = (n: number, format: (value: number) => string) => `${n >= 0 ? '+' : '−'}${format(Math.abs(n))}`;

const since = (start: IInForcePoint | null) => (start ? ` since ${moment(start.date).format('MMM D, YYYY')}` : '');

const countChange = ({ start, end }: IInForce) =>
	start && end ? `${signed(end.count - start.count, n => n.toLocaleString('en-US'))}${since(start)}` : undefined;

const premiumChange = ({ start, end }: IInForce) => {
	if (!start || !end || start.value === 0) {
		return undefined;
	}
	const percent = ((end.value - start.value) / start.value) * 100;
	return `${signed(percent, n => `${n.toFixed(1)}%`)}${since(start)}`;
};

export const HistoryKpis = ({
	inForce,
	policyHolders,
	retention,
	loading,
	changeFrom,
}: {
	inForce: IInForce;
	policyHolders: IInForce;
	retention: IRetention;
	loading?: boolean;
	/** Set before a full month has been tracked: the month change starts showing. */
	changeFrom?: string;
}) => {
	const pendingChange = changeFrom ? `Change shows from ${changeFrom}` : undefined;
	return (
		<div className={css(advancedReportingStyleSheet.container)}>
			<DataDisplay
				title='Premium in force'
				data={formatCurrency(inForce.end?.value ?? 0)}
				caption={pendingChange ?? premiumChange(inForce)}
				loading={loading}
				styles={[styleSheet.kpi]}
				className={css(styleSheet.kpi)}
			/>
			<DataDisplay
				title='Policies in force'
				data={(inForce.end?.count ?? 0).toLocaleString('en-US')}
				caption={pendingChange ?? countChange(inForce)}
				loading={loading}
				styles={[styleSheet.kpi]}
				className={css(styleSheet.kpi)}
			/>
			<DataDisplay
				title='Policyholders'
				data={(policyHolders.end?.count ?? 0).toLocaleString('en-US')}
				caption={pendingChange ?? countChange(policyHolders)}
				loading={loading}
				styles={[styleSheet.kpi]}
				className={css(styleSheet.kpi)}
			/>
			<DataDisplay
				title='Retention rate'
				data={retention.due ? `${Number(((retention.kept / retention.due) * 100).toFixed(1))}%` : '—'}
				caption={
					retention.due
						? `${retention.kept.toLocaleString('en-US')} of ${retention.due.toLocaleString('en-US')} renewals kept`
						: 'No renewals due in this period.'
				}
				loading={loading}
				styles={[styleSheet.kpi]}
				className={css(styleSheet.kpi)}
			/>
		</div>
	);
};
